import React, { useEffect, useRef, useState, useMemo } from 'react';
import './Videos.css';

interface VideoItem {
  id: string;
  title: string;
  description: string;
  videoId: string;
  category: string;
}

interface VideoPlayerState {
  [key: string]: {
    isMuted: boolean;
    player: any;
  };
}

// Declare YouTube API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const Videos: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [videoStates, setVideoStates] = useState<VideoPlayerState>({});
  const [apiLoaded, setApiLoaded] = useState(false);

  const videoItems: VideoItem[] = useMemo(() => [
    {
      id: '1',
      title: 'Channel 11 News - New DAY Diaz Art for Youth',
      description: 'Local television coverage highlighting Hector\'s community youth art program and its impact on aspiring young artists in the Tacoma area.',
      videoId: 'fYthcpdepOk',
      category: 'Media Coverage'
    },
    {
      id: '2',
      title: 'City of Destiny Award - New DAY-Diaz Art for Youth',
      description: 'Recognition ceremony for Hector\'s outstanding contribution to the community through his innovative art education program.',
      videoId: 'Yvm3OVUJwYM',
      category: 'Awards & Recognition'
    },
    {
      id: '3',
      title: 'Dr. Sharon McGavick Comments on New DAY-Diaz Art for Youth',
      description: 'Educational leader Dr. Sharon McGavick shares insights about the transformative impact of Hector\'s youth mentorship program.',
      videoId: 'siI5H4GbU54',
      category: 'Testimonials'
    }
  ], []);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(script);

      window.onYouTubeIframeAPIReady = () => {
        setApiLoaded(true);
      };
    } else {
      setApiLoaded(true);
    }
  }, []);

  // Initialize YouTube players
  useEffect(() => {
    if (apiLoaded && window.YT && window.YT.Player) {
      const newStates: VideoPlayerState = {};

      videoItems.forEach(video => {
        const playerId = `video-${video.id}`;
        const existingElement = document.getElementById(playerId);

        if (existingElement) {
          const player = new window.YT.Player(playerId, {
            videoId: video.videoId,
            playerVars: {
              autoplay: 1,
              mute: 1,
              loop: 1,
              playlist: video.videoId,
              rel: 0,
              modestbranding: 1,
              showinfo: 0,
              controls: 1,
              enablejsapi: 1
            },
            events: {
              onReady: (event: any) => {
                event.target.playVideo();
                event.target.mute();
              }
            }
          });

          newStates[video.id] = {
            isMuted: true,
            player: player
          };
        }
      });

      setVideoStates(newStates);
    }
  }, [apiLoaded, videoItems]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.video-item');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Media Coverage': return '📺';
      case 'Awards & Recognition': return '🏆';
      case 'Testimonials': return '💬';
      default: return '🎥';
    }
  };

  const handleVideoToggle = (videoId: string) => {
    const currentState = videoStates[videoId];

    if (!currentState?.player) return;

    const player = currentState.player;

    if (currentState.isMuted) {
      // If currently muted, unmute without changing position
      player.unMute();
      setVideoStates(prev => ({
        ...prev,
        [videoId]: { ...prev[videoId], isMuted: false }
      }));
    } else {
      // If currently unmuted, mute without changing position
      player.mute();
      setVideoStates(prev => ({
        ...prev,
        [videoId]: { ...prev[videoId], isMuted: true }
      }));
    }
  };


  return (
    <section className="videos-section" id="videos" ref={sectionRef}>
      <div className="videos-container">
        <div className="videos-header hector-fade-in-up">
          <h2 className="hector-section-title videos-title">Media & Recognition</h2>
          <p className="videos-subtitle">
            Discover Hector Díaz's community impact through television coverage,
            awards ceremonies, and testimonials from educational leaders
          </p>
          <div className="hector-horizon-line"></div>
        </div>

        <div className="videos-grid">
          {videoItems.map((video, index) => (
            <div
              key={video.id}
              className="video-item-large"
              style={{ animationDelay: `${index * 0.3}s` }}
            >
              <div className="video-player-container-large">
                <div className="video-wrapper-large">
                  <div
                    id={`video-${video.id}`}
                    className="video-player-large"
                  />

                  {/* Minimal overlay */}
                  <div className="video-overlay-minimal">
                    <div className="video-info-minimal">
                      <h3 className="video-title-minimal">{video.title}</h3>
                      <div className="video-controls-minimal">
                        <button
                          className={`mute-toggle-minimal ${videoStates[video.id]?.isMuted ? 'muted' : 'unmuted'}`}
                          onClick={() => handleVideoToggle(video.id)}
                          title={videoStates[video.id]?.isMuted ? 'Unmute' : 'Mute'}
                        >
                          {videoStates[video.id]?.isMuted ? '🔇' : '🔊'}
                        </button>
                        <button
                          className="learn-more-btn"
                          onClick={() => {
                            const detailsElement = document.getElementById(`details-${video.id}`);
                            if (detailsElement) {
                              detailsElement.classList.toggle('show-details');
                            }
                          }}
                        >
                          Learn More
                        </button>
                      </div>
                    </div>

                    {/* Expandable details */}
                    <div id={`details-${video.id}`} className="video-details-expandable">
                      <div className="video-category-large">
                        <span className="category-icon">{getCategoryIcon(video.category)}</span>
                        <span className="category-text">{video.category}</span>
                      </div>
                      <p className="video-description-large">{video.description}</p>
                      <div className="video-status">
                        {videoStates[video.id]?.isMuted
                          ? 'Playing muted in loop'
                          : 'Playing with sound'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="videos-impact hector-fade-in-up">
          <div className="impact-card">
            <h3 className="impact-title">Community Impact Through Media</h3>
            <p className="impact-description">
              These videos document Hector's lasting influence on the Tacoma community,
              showcasing how his "New DAY - Diaz Art for Youth" program transformed
              countless young lives and earned recognition from civic leaders and educators.
            </p>
            <div className="impact-stats">
              <div className="stat-item">
                <span className="stat-number">3</span>
                <span className="stat-label">Media Features</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1</span>
                <span className="stat-label">City Award</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">∞</span>
                <span className="stat-label">Lives Impacted</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background decorations */}
      <div className="videos-decorations">
        <div className="floating-video-elements">
          <div className="video-star star-1"></div>
          <div className="video-star star-2"></div>
          <div className="video-star star-3"></div>
        </div>
      </div>
    </section>
  );
};

export default Videos;